/**
 * Authentication Provider
 * Manages authentication state and provides auth methods
 */

import { useState, useEffect, ReactNode } from 'react'
import { User, LoginFormData, SignupFormData } from '@/types'
import { userService } from '@/services'
import { AuthContext, AuthContextType } from './AuthContext.context'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (userService.isAuthenticated()) {
          const currentUser = await userService.getCurrentUser()
          setUser(currentUser)
        }
      } catch (error) {
        console.error('Error checking authentication:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (credentials: LoginFormData) => {
    await userService.login(credentials)
    const currentUser = await userService.getCurrentUser()
    setUser(currentUser)
  }

  const signup = async (data: SignupFormData) => {
    await userService.signup(data)
    const currentUser = await userService.getCurrentUser()
    setUser(currentUser)
  }

  const logout = async () => {
    await userService.logout()
    setUser(null)
  }

  const refreshUser = async () => {
    try {
      const currentUser = await userService.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Error refreshing user:', error)
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading,
    login,
    signup,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
